import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Card, Grid } from "@mui/material";
import { CheckCircleOutline, Delete, PersonAdd, Visibility } from "@mui/icons-material";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import { toast } from "react-toastify";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import PaginationBar from "components/PaginationBar/PaginationBar";
import Dropdown from "components/CustomDropdown/Dropdown";
import PremiumDialog from "components/PremiumDialog/PremiumDialog";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import axios, { baseURL } from "utils/api";
import atsTableData from "Views/Ats/data/atsTableData";
import {
  ATS_INT_STATUSES,
  ATS_SCREENING,
  ATS_SOURCES,
  candidateToForm,
  emptyCandidateForm,
} from "Views/Ats/constants";

const FIELD_LABELS = {
  source: "Source",
  screeningQualified: "Screening Qualified",
  intStatus: "Int Status",
};

const withBlank = (label, values) => [
  { label, value: "" },
  ...values.map((value) => ({ label: value, value })),
];

const BULK_FIELDS = [
  { value: "source", label: "Source" },
  { value: "screeningQualified", label: "Screening Qualified" },
  { value: "intStatus", label: "Int Status" },
];

const bulkOptionsFor = (field) => {
  if (field === "source") return ATS_SOURCES;
  if (field === "screeningQualified") return ATS_SCREENING;
  if (field === "intStatus") return ATS_INT_STATUSES;
  return [];
};

function FieldLabel({ children }) {
  return (
    <ArgonTypography
      variant="body2"
      fontWeight="medium"
      color="text"
      mb={0.75}
      display="block"
      sx={{ textTransform: "none" }}
    >
      {children}
    </ArgonTypography>
  );
}

FieldLabel.propTypes = {
  children: PropTypes.node,
};

const FILE_ACCEPT =
  ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

function UploadCard({ label, hint, fileName, emptyText, onView, onChange, actionLabel }) {
  return (
    <ArgonBox height="100%" display="flex" flexDirection="column">
      <FieldLabel>{label}</FieldLabel>
      <ArgonBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1.5}
        flexWrap="wrap"
        sx={{
          flex: 1,
          border: "1px dashed #b9c6d8",
          borderRadius: "12px",
          backgroundColor: "#f7faff",
          px: 2,
          py: 1.5,
        }}
      >
        <ArgonBox sx={{ minWidth: 0 }}>
          <ArgonTypography
            variant="button"
            color="text"
            fontWeight="medium"
            title={fileName || ""}
            sx={{
              textTransform: "none",
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 220,
            }}
          >
            {fileName || emptyText}
          </ArgonTypography>
          <ArgonTypography variant="caption" color="secondary" display="block">
            {hint}
          </ArgonTypography>
        </ArgonBox>
        <ArgonBox display="flex" alignItems="center" gap={1}>
          {onView && (
            <ArgonButton
              variant="outlined"
              color="info"
              size="small"
              onClick={onView}
              startIcon={<Visibility />}
            >
              View
            </ArgonButton>
          )}
          <ArgonButton component="label" variant="gradient" color="info" size="small">
            {actionLabel}
            <input hidden type="file" accept={FILE_ACCEPT} onChange={onChange} />
          </ArgonButton>
        </ArgonBox>
      </ArgonBox>
    </ArgonBox>
  );
}

UploadCard.propTypes = {
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
  fileName: PropTypes.string,
  emptyText: PropTypes.string,
  onView: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  actionLabel: PropTypes.string.isRequired,
};

function DetailTile({ label, value }) {
  return (
    <ArgonBox
      sx={{
        height: "100%",
        border: "1px solid #e6edf5",
        borderRadius: "12px",
        backgroundColor: "#fbfcfe",
        px: 1.75,
        py: 1.25,
      }}
    >
      <ArgonTypography variant="caption" color="secondary" fontWeight="medium" display="block">
        {label}
      </ArgonTypography>
      <ArgonTypography
        variant="button"
        color="dark"
        fontWeight="bold"
        sx={{ textTransform: "none", wordBreak: "break-word" }}
      >
        {value || "-"}
      </ArgonTypography>
    </ArgonBox>
  );
}

DetailTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

const readSuperAdmin = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return user?.role === "SUPER_ADMIN";
  } catch (error) {
    return false;
  }
};

function InHouseAts() {
  const [isSuperAdmin] = useState(readSuperAdmin);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("");
  const [screening, setScreening] = useState("");
  const [intStatus, setIntStatus] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCandidateForm);
  const [resumeFile, setResumeFile] = useState(null);
  const [templateFile, setTemplateFile] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkField, setBulkField] = useState("");
  const [bulkValue, setBulkValue] = useState("");

  const fetchCandidates = useCallback(async () => {
    if (!isSuperAdmin) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(rowsPerPage),
      });
      if (search.trim()) params.set("search", search.trim());
      if (source) params.set("source", source);
      if (screening) params.set("screeningQualified", screening);
      if (intStatus) params.set("intStatus", intStatus);

      const response = await axios.get(`${baseURL}/in-house-ats?${params.toString()}`);
      setCandidates(response.data.data || []);
      setTotalItems(response.data.pagination?.totalCount || 0);
      setTotalPages(response.data.pagination?.totalPages || 0);
    } catch (error) {
      setCandidates([]);
      setTotalItems(0);
      setTotalPages(0);
      toast.error(error.response?.data?.message || "Failed to load candidates");
    } finally {
      setLoading(false);
    }
  }, [currentPage, intStatus, isSuperAdmin, rowsPerPage, screening, search, source]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, source, screening, intStatus]);

  useEffect(() => {
    setSelectedIds([]);
  }, [currentPage, search, source, screening, intStatus]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyCandidateForm);
    setResumeFile(null);
    setTemplateFile(null);
    setDialogOpen(true);
  };

  const openEdit = (candidate) => {
    setViewing(null);
    setEditing(candidate);
    setForm(candidateToForm(candidate));
    setResumeFile(null);
    setTemplateFile(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditing(null);
    setForm(emptyCandidateForm);
    setResumeFile(null);
    setTemplateFile(null);
  };

  const updateField = (field) => (event) => {
    const nextValue = event.target.value;
    setForm((current) =>
      current[field] === nextValue ? current : { ...current, [field]: nextValue }
    );
  };

  const handleSave = async () => {
    if (!form.candidateName.trim()) {
      toast.error("Candidate name is required");
      return;
    }

    const rating = form.introRating === "" ? "" : Number(form.introRating);
    if (rating !== "" && (Number.isNaN(rating) || rating < 0 || rating > 10)) {
      toast.error("Intro rating must be between 0 and 10");
      return;
    }
    if (!editing && !resumeFile) {
      toast.error("Upload a resume");
      return;
    }

    setSaving(true);
    try {
      const payload = new FormData();
      payload.append("candidateName", form.candidateName.trim());
      payload.append("jobProfile", form.jobProfile.trim());
      payload.append("callingRemark", form.callingRemark);
      payload.append("source", form.source);
      payload.append("screeningQualified", form.screeningQualified);
      payload.append("intStatus", form.intStatus);
      payload.append("locality", form.locality);
      payload.append("introRating", rating === "" ? "" : String(rating));
      if (resumeFile) payload.append("resume", resumeFile);
      if (templateFile) payload.append("preRoundTemplate", templateFile);

      const config = { headers: { "Content-Type": "multipart/form-data" } };
      if (editing?._id) {
        await axios.put(`${baseURL}/in-house-ats/${editing._id}`, payload, config);
        toast.success("Candidate updated");
      } else {
        await axios.post(`${baseURL}/in-house-ats`, payload, config);
        toast.success("Candidate added");
      }
      setDialogOpen(false);
      setEditing(null);
      setForm(emptyCandidateForm);
      setResumeFile(null);
      setTemplateFile(null);
      fetchCandidates();
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not save candidate");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenFile = async (candidate, kind) => {
    const isTemplate = kind === "template";
    try {
      const path = isTemplate ? "pre-round-template" : "resume";
      const response = await axios.get(`${baseURL}/in-house-ats/${candidate._id}/${path}`);
      const url = response.data?.data?.url;
      if (!url) {
        toast.error(isTemplate ? "Pre Round Template not found" : "Resume not found");
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          (isTemplate ? "Could not open pre round template" : "Could not open resume")
      );
    }
  };

  const askFieldChange = (candidate, field, nextValue) => {
    if ((candidate[field] || "") === nextValue) return;
    setConfirmState({ type: "update", candidate, field, nextValue });
  };

  const askDelete = (candidate) => {
    setConfirmState({ type: "delete", candidate });
  };

  const closeConfirm = () => {
    if (confirming) return;
    setConfirmState(null);
  };

  const applyConfirm = async () => {
    if (!confirmState || confirming) return;
    setConfirming(true);
    try {
      if (confirmState.type === "delete") {
        await axios.delete(`${baseURL}/in-house-ats/${confirmState.candidate._id}`);
        toast.success("Candidate removed");
        fetchCandidates();
      } else if (confirmState.type === "bulk-delete") {
        const results = await Promise.allSettled(
          selectedIds.map((id) => axios.delete(`${baseURL}/in-house-ats/${id}`))
        );
        const failed = results.filter((result) => result.status === "rejected").length;
        if (failed) toast.error(`${failed} candidate(s) could not be removed`);
        else toast.success("Selected candidates removed");
        setSelectedIds([]);
        fetchCandidates();
      } else if (confirmState.type === "bulk-update") {
        const results = await Promise.allSettled(
          selectedIds.map((id) =>
            axios.put(`${baseURL}/in-house-ats/${id}`, {
              [confirmState.field]: confirmState.nextValue,
            })
          )
        );
        const failed = results.filter((result) => result.status === "rejected").length;
        if (failed) toast.error(`${failed} candidate(s) could not be updated`);
        else toast.success("Selected candidates updated");
        setSelectedIds([]);
        setBulkField("");
        setBulkValue("");
        fetchCandidates();
      } else {
        const response = await axios.put(
          `${baseURL}/in-house-ats/${confirmState.candidate._id}`,
          { [confirmState.field]: confirmState.nextValue }
        );
        const updated = response.data?.data;
        if (updated?._id) {
          setCandidates((current) =>
            current.map((item) => (item._id === updated._id ? updated : item))
          );
        } else {
          fetchCandidates();
        }
        toast.success("Updated");
      }
      setConfirmState(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not update the tracker");
    } finally {
      setConfirming(false);
    }
  };

  const pageIds = candidates.map((candidate) => candidate._id);
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const confirmCopy = (() => {
    if (confirmState?.type === "delete") {
      return {
        title: "Remove candidate?",
        description: `Remove ${confirmState.candidate.candidateName} from the tracker?`,
        warningText: "The candidate will no longer appear in this list.",
        confirmText: confirming ? "Removing..." : "Remove",
        confirmColor: "error",
        confirmIcon: <Delete />,
      };
    }
    if (confirmState?.type === "bulk-delete") {
      return {
        title: "Remove selected candidates?",
        description: `Remove ${selectedIds.length} selected candidate(s) from the tracker?`,
        warningText: "They will no longer appear in this list.",
        confirmText: confirming ? "Removing..." : "Remove",
        confirmColor: "error",
        confirmIcon: <Delete />,
      };
    }
    if (confirmState?.type === "bulk-update") {
      return {
        title: "Update selected candidates?",
        description: `Change ${FIELD_LABELS[confirmState.field]} to ${confirmState.nextValue || "blank"} for ${selectedIds.length} selected candidate(s)?`,
        warningText: "The same value is saved on every selected row.",
        confirmText: confirming ? "Updating..." : "Update",
        confirmColor: "info",
        confirmIcon: <CheckCircleOutline />,
      };
    }
    if (confirmState?.type === "update") {
      return {
        title: "Update candidate?",
        description: `Change ${FIELD_LABELS[confirmState.field]} for ${confirmState.candidate.candidateName} from ${confirmState.candidate[confirmState.field] || "blank"} to ${confirmState.nextValue || "blank"}?`,
        warningText: "The new value is saved only after you confirm.",
        confirmText: confirming ? "Updating..." : "Update",
        confirmColor: "info",
        confirmIcon: <CheckCircleOutline />,
      };
    }
    return {
      title: "",
      description: "",
      warningText: "",
      confirmText: "Confirm",
      confirmColor: "info",
      confirmIcon: <CheckCircleOutline />,
    };
  })();

  const { columns, rows } = atsTableData(
    candidates,
    (currentPage - 1) * rowsPerPage,
    setViewing,
    openEdit,
    askDelete,
    (candidate) => handleOpenFile(candidate, "resume"),
    (candidate) => handleOpenFile(candidate, "template"),
    askFieldChange,
    selectionMode
      ? {
          enabled: true,
          selectedIds,
          allSelected,
          onToggleAll: (checked) => setSelectedIds(checked ? pageIds : []),
          onToggleOne: (id, checked) =>
            setSelectedIds((current) =>
              checked ? [...current, id] : current.filter((itemId) => itemId !== id)
            ),
        }
      : null
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={2}
              >
                <ArgonBox>
                  <ArgonTypography variant="h6">In-House ATS</ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Calling status tracker for in-house hiring
                  </ArgonTypography>
                </ArgonBox>
                {isSuperAdmin && (
                  <ArgonButton variant="gradient" color="info" size="small" onClick={openCreate}>
                    Add Candidate
                  </ArgonButton>
                )}
              </ArgonBox>

              {isSuperAdmin && (
                <>
                <ArgonBox display="flex" alignItems="center" gap={2} mt={2} flexWrap="wrap">
                  <ArgonBox sx={{ width: "240px" }}>
                    <ArgonInput
                      placeholder="Search name, profile, comment, locality"
                      size="small"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                    />
                  </ArgonBox>
                  <Dropdown
                    value={source}
                    options={withBlank("All Sources", ATS_SOURCES)}
                    onChange={(event) => setSource(event.target.value)}
                    width={210}
                  />
                  <Dropdown
                    value={screening}
                    options={withBlank("All Screening", ATS_SCREENING)}
                    onChange={(event) => setScreening(event.target.value)}
                    width={160}
                  />
                  <Dropdown
                    value={intStatus}
                    options={withBlank("All Int Status", ATS_INT_STATUSES)}
                    onChange={(event) => setIntStatus(event.target.value)}
                    width={220}
                  />
                </ArgonBox>
                <ArgonBox display="flex" alignItems="center" justifyContent="flex-end" flexWrap="wrap" gap={1} mt={2}>
                  {selectionMode && selectedIds.length > 0 && (
                    <>
                      <ArgonBox
                        role="status"
                        aria-label={`${selectedIds.length} candidates selected`}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.75,
                          height: 32,
                          px: 1.25,
                          borderRadius: "8px",
                          bgcolor: "#eaf6ff",
                          border: "1px solid #cce7fa",
                          color: "#1673b1",
                        }}
                      >
                        <CheckCircleOutline sx={{ fontSize: 16 }} />
                        <ArgonBox component="span" sx={{ fontSize: 12, fontWeight: 600 }}>
                          Selected
                        </ArgonBox>
                        <ArgonBox
                          component="span"
                          sx={{
                            minWidth: 22,
                            height: 22,
                            px: 0.5,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "6px",
                            bgcolor: "#1673b1",
                            color: "#fff",
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          {selectedIds.length}
                        </ArgonBox>
                      </ArgonBox>
                      <select
                        aria-label="Field for selected candidates"
                        value={bulkField}
                        disabled={confirming}
                        onChange={(event) => {
                          setBulkField(event.target.value);
                          setBulkValue("");
                        }}
                        style={{
                          width: 180,
                          height: 32,
                          padding: "4px 8px",
                          border: "1px solid #d2d6da",
                          borderRadius: 6,
                          backgroundColor: "#fff",
                          color: "#344767",
                          fontSize: 13,
                        }}
                      >
                        <option value="">Choose field</option>
                        {BULK_FIELDS.map((field) => (
                          <option key={field.value} value={field.value}>
                            {field.label}
                          </option>
                        ))}
                      </select>
                      <select
                        aria-label="Value for selected candidates"
                        value={bulkValue}
                        disabled={confirming || !bulkField}
                        onChange={(event) => setBulkValue(event.target.value)}
                        style={{
                          width: 190,
                          height: 32,
                          padding: "4px 8px",
                          border: "1px solid #d2d6da",
                          borderRadius: 6,
                          backgroundColor: bulkField ? "#fff" : "#f6f7f9",
                          color: "#344767",
                          fontSize: 13,
                        }}
                      >
                        <option value="">Choose value</option>
                        {bulkOptionsFor(bulkField).map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      <ArgonButton
                        size="small"
                        color="info"
                        variant="contained"
                        disabled={confirming || !bulkField || !bulkValue}
                        onClick={() =>
                          setConfirmState({ type: "bulk-update", field: bulkField, nextValue: bulkValue })
                        }
                      >
                        Update Selected
                      </ArgonButton>
                      <ArgonButton
                        size="small"
                        color="error"
                        variant="outlined"
                        disabled={confirming}
                        onClick={() => setConfirmState({ type: "bulk-delete" })}
                      >
                        Remove Selected
                      </ArgonButton>
                    </>
                  )}
                  <ArgonButton
                    size="small"
                    color="secondary"
                    variant="outlined"
                    disabled={confirming}
                    aria-pressed={selectionMode}
                    onClick={() => {
                      setSelectionMode((current) => !current);
                      setSelectedIds([]);
                      setBulkField("");
                      setBulkValue("");
                    }}
                  >
                    {selectionMode ? "Cancel Selection" : "Select Candidates"}
                  </ArgonButton>
                </ArgonBox>
                </>
              )}
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {!isSuperAdmin ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Super admin access only
                  </ArgonTypography>
                </ArgonBox>
              ) : loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Fetching candidates...
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Please wait while we load the tracker.
                  </ArgonTypography>
                </ArgonBox>
              ) : rows.length > 0 ? (
                <Table columns={columns} rows={rows} />
              ) : (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    {search || source || screening || intStatus
                      ? "No candidates found for the selected filters"
                      : "No candidates yet"}
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    {search || source || screening || intStatus
                      ? "Try adjusting your filters."
                      : "Add the first in-house candidate to start the tracker."}
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>

            {isSuperAdmin && totalItems > 0 && (
              <PaginationBar
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                totalItems={totalItems}
                totalPages={totalPages}
                onPageChange={(event, page) => setCurrentPage(page)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(event.target.value);
                  setCurrentPage(1);
                }}
              />
            )}
          </Card>
        </ArgonBox>
      </ArgonBox>

      <PremiumDialog
        open={dialogOpen}
        onClose={closeDialog}
        title={editing ? "Edit Candidate" : "Add Candidate"}
        icon={PersonAdd}
        maxWidth="md"
      >
        <Grid container spacing={2.5} alignItems="flex-start">
          <Grid item xs={12} sm={6}>
            <FieldLabel>Candidate Name</FieldLabel>
            <ArgonInput
              value={form.candidateName}
              onChange={updateField("candidateName")}
              placeholder="Candidate name"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Job Profile</FieldLabel>
            <ArgonInput
              value={form.jobProfile}
              onChange={updateField("jobProfile")}
              placeholder="HR and Growth Manager"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Source</FieldLabel>
            <Dropdown
              value={form.source}
              options={withBlank("Select source", ATS_SOURCES)}
              onChange={updateField("source")}
              width="100%"
              minHeight="32px"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Screening Qualified</FieldLabel>
            <Dropdown
              value={form.screeningQualified}
              options={withBlank("Select", ATS_SCREENING)}
              onChange={updateField("screeningQualified")}
              width="100%"
              minHeight="32px"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Int Status</FieldLabel>
            <Dropdown
              value={form.intStatus}
              options={withBlank("Select status", ATS_INT_STATUSES)}
              onChange={updateField("intStatus")}
              width="100%"
              minHeight="32px"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Intro Ratings (/10)</FieldLabel>
            <ArgonInput
              value={form.introRating}
              onChange={updateField("introRating")}
              placeholder="8"
              type="number"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <FieldLabel>Locality in Bhopal</FieldLabel>
            <ArgonInput
              value={form.locality}
              onChange={updateField("locality")}
              placeholder="Locality"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <FieldLabel>Comment</FieldLabel>
            <textarea
              value={form.callingRemark}
              onChange={updateField("callingRemark")}
              placeholder="Add a comment"
              rows={5}
              style={{
                width: "100%",
                boxSizing: "border-box",
                minHeight: 120,
                resize: "vertical",
                border: "1px solid #d2d6da",
                borderRadius: "8px",
                padding: "10px 12px",
                fontSize: "14px",
                lineHeight: 1.5,
                fontFamily: "inherit",
                color: "#344767",
                outline: "none",
                backgroundColor: "#fff",
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <UploadCard
              label="Resume"
              hint="PDF, DOC, or DOCX, up to 10 MB"
              fileName={resumeFile?.name || editing?.resumeFileName || ""}
              emptyText="No resume selected"
              actionLabel={resumeFile || editing?.hasResume ? "Replace" : "Upload"}
              onView={editing?.hasResume && !resumeFile ? () => handleOpenFile(editing, "resume") : null}
              onChange={(event) => setResumeFile(event.target.files?.[0] || null)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <UploadCard
              label="Pre Round Template"
              hint="PDF, DOC, or DOCX, up to 10 MB"
              fileName={templateFile?.name || editing?.preRoundTemplateFileName || ""}
              emptyText="No template selected"
              actionLabel={templateFile || editing?.hasPreRoundTemplate ? "Replace" : "Upload"}
              onView={
                editing?.hasPreRoundTemplate && !templateFile
                  ? () => handleOpenFile(editing, "template")
                  : null
              }
              onChange={(event) => setTemplateFile(event.target.files?.[0] || null)}
            />
          </Grid>
          <Grid item xs={12}>
            <ArgonBox display="flex" justifyContent="flex-end" gap={1.5} mt={0.5}>
              <ArgonButton variant="outlined" color="secondary" onClick={closeDialog} disabled={saving}>
                Cancel
              </ArgonButton>
              <ArgonButton variant="gradient" color="info" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : editing ? "Save changes" : "Add candidate"}
              </ArgonButton>
            </ArgonBox>
          </Grid>
        </Grid>
      </PremiumDialog>

      <PremiumDialog
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title="Candidate Details"
        icon={Visibility}
        maxWidth="md"
      >
        {viewing && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ArgonTypography variant="h5" color="dark" fontWeight="bold">
                {viewing.candidateName}
              </ArgonTypography>
              <ArgonTypography variant="button" color="secondary" sx={{ textTransform: "none" }}>
                {viewing.jobProfile || "No job profile"}
              </ArgonTypography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DetailTile label="Source" value={viewing.source} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DetailTile label="Screening Qualified" value={viewing.screeningQualified} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DetailTile label="Int Status" value={viewing.intStatus} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DetailTile label="Locality in Bhopal" value={viewing.locality} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DetailTile
                label="Intro Ratings (/10)"
                value={
                  viewing.introRating === 0 || viewing.introRating ? viewing.introRating : "-"
                }
              />
            </Grid>
            <Grid item xs={12}>
              <ArgonBox
                sx={{
                  border: "1px solid #e6edf5",
                  borderRadius: "12px",
                  backgroundColor: "#fbfcfe",
                  px: 1.75,
                  py: 1.5,
                }}
              >
                <ArgonTypography variant="caption" color="secondary" fontWeight="medium" display="block" mb={0.75}>
                  Comment
                </ArgonTypography>
                <ArgonTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                  sx={{
                    textTransform: "none",
                    whiteSpace: "pre-wrap",
                    display: "block",
                    maxHeight: 180,
                    overflow: "auto",
                    lineHeight: 1.6,
                  }}
                >
                  {viewing.callingRemark || "No comment"}
                </ArgonTypography>
              </ArgonBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailTile
                label="Resume"
                value={viewing.hasResume ? viewing.resumeFileName || "Resume uploaded" : "Not uploaded"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailTile
                label="Pre Round Template"
                value={
                  viewing.hasPreRoundTemplate
                    ? viewing.preRoundTemplateFileName || "Template uploaded"
                    : "Not uploaded"
                }
              />
            </Grid>
            <Grid item xs={12}>
              <ArgonBox display="flex" justifyContent="flex-end" flexWrap="wrap" gap={1.5}>
                {viewing.hasResume && (
                  <ArgonButton
                    variant="outlined"
                    color="info"
                    size="small"
                    startIcon={<Visibility />}
                    onClick={() => handleOpenFile(viewing, "resume")}
                  >
                    Resume
                  </ArgonButton>
                )}
                {viewing.hasPreRoundTemplate && (
                  <ArgonButton
                    variant="outlined"
                    color="info"
                    size="small"
                    startIcon={<Visibility />}
                    onClick={() => handleOpenFile(viewing, "template")}
                  >
                    Template
                  </ArgonButton>
                )}
                <ArgonButton variant="outlined" color="secondary" onClick={() => setViewing(null)}>
                  Close
                </ArgonButton>
                <ArgonButton
                  variant="gradient"
                  color="info"
                  startIcon={<DriveFileRenameOutlineIcon />}
                  onClick={() => openEdit(viewing)}
                >
                  Edit
                </ArgonButton>
              </ArgonBox>
            </Grid>
          </Grid>
        )}
      </PremiumDialog>

      <DeleteDialog
        open={Boolean(confirmState)}
        onClose={closeConfirm}
        onConfirm={applyConfirm}
        title={confirmCopy.title}
        description={confirmCopy.description}
        warningText={confirmCopy.warningText}
        confirmText={confirmCopy.confirmText}
        cancelText="Cancel"
        confirmIcon={confirmCopy.confirmIcon}
        confirmColor={confirmCopy.confirmColor}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default InHouseAts;
